import type { Patient } from '@medplum/fhirtypes'
import { featureFlags } from '@seen/core/featureFlags'
import { invariant } from '@seen/core/invariant'
import { Participant } from '@seen/core/models/patients/participant'
import { parseReferenceString } from '@seen/core/utils'
import { useEffect } from 'react'
import { useParams } from 'react-router'
import { Loader } from '#src/components/core/Loader.tsx'
import { ParticipantNotFound } from '#src/components/core/ParticipantNotFound.tsx'
import { CurrentParticipantContext } from '#src/contexts/current-participant/CurrentParticipantContext.tsx'
import { useErrorToast } from '#src/hooks/useErrorToast.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useParticipantModalFlags } from '#src/hooks/useParticipantModalFlags.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { useTaskComposerStore } from '#src/hooks/useTaskComposerStore.ts'

export const CurrentParticipantProvider: React.FC<{
  children: React.ReactNode
  patientId?: string
}> = ({ children, patientId: overridePatientId }) => {
  const { patientId: pathPatientId } = useParams<{ patientId: string }>()
  const patientId = overridePatientId ?? pathPatientId
  invariant(
    patientId,
    'You must have patientId in the path params to use `useCurrentParticipant`'
  )

  const {
    data: participant,
    isLoading,
    error,
  } = useRead(Participant, patientId)
  const isOosaEnabled = useFeatureFlag(featureFlags.facesheetOosaFlag)
  const isCriticalIsolationEnabled = useFeatureFlag(
    featureFlags.facesheetCriticalIsolationFlags
  )
  // Start the active Flag search with the Patient read so the OOSA date
  // range and Critical Attention / Isolation hover details are already
  // cached when the header paints.
  useParticipantModalFlags(
    isOosaEnabled || isCriticalIsolationEnabled
      ? parseReferenceString<Patient>(`Patient/${patientId}`)
      : undefined
  )

  // The task composer isn't a child of this component, so we set it globally.
  // TODO: Have a better pattern around this.
  const setTaskComposerCurrentParticipant = useTaskComposerStore(
    (state) => state.setCurrentParticipant
  )
  useEffect(() => {
    const reference = patientId
      ? parseReferenceString<Patient>(`Patient/${patientId}`)
      : undefined
    setTaskComposerCurrentParticipant(reference)
  }, [patientId, setTaskComposerCurrentParticipant])

  useErrorToast(
    !isLoading && !participant
      ? `Could not find participant with id: ${patientId}`
      : undefined
  )

  if (isLoading) {
    return <Loader />
  }
  if (!participant) {
    return <ParticipantNotFound participantId={patientId} />
  }
  if (error) {
    throw error
  }

  return (
    <CurrentParticipantContext.Provider value={participant}>
      {children}
    </CurrentParticipantContext.Provider>
  )
}
